
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  type Payment,
  usePayments,
  useDeletePayment,
  useUpdatePayment,
} from '@/api/payments';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Plus,
  CalendarIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useConfirmationStore } from '@/lib/stores/confirmation-store';
import { useState, useEffect } from 'react';
import { AddInstallmentDialog } from './add-installment-dialog';
import { EditPaymentDateDialog } from './edit-payment-date-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency } from '@/lib/format-currency';
import { useGym } from '@/contexts/gym-context';
import { DataTablePagination } from '@/components/ui/data-table-pagination';

interface PaymentTableProps {
  startDate: string;
  endDate: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  comparisonMode?: boolean;
}

export function PaymentTable({
  startDate,
  endDate,
  comparisonStartDate = '',
  comparisonEndDate = '',
  comparisonMode = false,
}: PaymentTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectedGym } = useGym();
  const { data: payments, isLoading: paymentsLoading } = usePayments(
    startDate,
    endDate,
    selectedGym
  );

  const deletePaymentMutation = useDeletePayment();
  const updatePaymentMutation = useUpdatePayment();
  const confirmationStore = useConfirmationStore();
  const queryClient = useQueryClient();

  // Fetch comparison data when comparison mode is enabled
  const { data: comparisonPayments, isLoading: comparisonLoading } =
    usePayments(
      comparisonStartDate || '01-01-2020',
      comparisonEndDate || '01-01-2020',
      selectedGym
    );

  // State for expanded rows and add installment dialog
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [addInstallmentDialogOpen, setAddInstallmentDialogOpen] =
    useState(false);
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedGym, statusFilter]);

  // Toggle row expansion
  const toggleRowExpansion = (paymentId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [paymentId]: !prev[paymentId],
    }));
  };

  // Get user name from payment
  const getUserName = (payment: Payment) => {
    if (typeof payment.userId === 'object' && payment.userId !== null) {
      const user = payment.userId;
      return `${user.name} ${user.surname || ''}`.trim();
    }
    return 'Unknown User';
  };

  // Get plan name from payment
  const getPlanName = (payment: Payment) => {
    if (typeof payment.planId === 'object' && payment.planId !== null) {
      return payment.planId.name;
    }
    return 'Unknown Plan';
  };

  // Get plan price from payment
  const getPlanPrice = (payment: Payment) => {
    if (typeof payment.planId === 'object' && payment.planId !== null) {
      return payment.planId.price || 0;
    }
    return 0;
  };

  // Get plan currency from payment
  const getPlanCurrency = (payment: Payment) => {
    if (typeof payment.planId === 'object' && payment.planId !== null) {
      return payment.planId.currency || 'ALL';
    }
    return 'ALL';
  };

  // Get the appropriate date to display based on selection type
  const getDisplayDate = (payment: Payment) => {
    const formatDateTime = (date: Date) => {
      const dateStr = date.toLocaleDateString();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dateStr} ${hours}:${minutes}`;
    };

    // If single date (startDate === endDate), show installment date
    if (
      startDate === endDate &&
      payment.installments &&
      payment.installments.length > 0
    ) {
      // Parse the selected date properly
      const parseDate = (dateStr: string) => {
        const [day, month, year] = dateStr.split('-');
        return new Date(`${year}-${month}-${day}`);
      };

      const selectedDate = parseDate(startDate);
      selectedDate.setHours(0, 0, 0, 0);

      // Find installment that matches the selected date
      const matchingInstallment = payment.installments.find((inst) => {
        const installmentDate = new Date(inst.date);
        installmentDate.setHours(0, 0, 0, 0);
        return installmentDate.getTime() === selectedDate.getTime();
      });

      if (matchingInstallment) {
        return formatDateTime(new Date(matchingInstallment.date));
      }
    }

    // For date ranges or payments without installments, show payment.date
    return formatDateTime(new Date(payment.date));
  };

  // Calculate paid amount from installments within the selected date range
  const getPaidAmount = (payment: Payment) => {
    if (!payment.installments || payment.installments.length === 0) {
      return payment.amount || 0;
    }

    // Parse dates properly - convert from dd-mm-yyyy to yyyy-mm-dd
    const parseDate = (dateStr: string) => {
      const [day, month, year] = dateStr.split('-');
      return new Date(`${year}-${month}-${day}`);
    };

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    // Set time to start/end of day for proper comparison
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const total = payment.installments.reduce((sum, inst) => {
      const installmentDate = new Date(inst.date);
      installmentDate.setHours(0, 0, 0, 0);

      // Check if installment date is within range
      if (installmentDate >= start && installmentDate <= end) {
        return sum + (Number(inst.amount) || 0);
      }
      return sum;
    }, 0);

    // If no installments were found within the date range but payment exists,
    // return sum of all installments as fallback
    if (total === 0 && payment.installments.length > 0) {
      const fallbackTotal = payment.installments.reduce((sum, inst) => {
        return sum + (Number(inst.amount) || 0);
      }, 0);
      return fallbackTotal;
    }

    return total;
  };

  // Calculate total amount for a payment array within date range
  const calculateTotalAmount = (
    paymentsArray: Payment[] = [],
    dateRangeStart: string,
    dateRangeEnd: string
  ) => {
    if (!paymentsArray || paymentsArray.length === 0) return 0;

    return paymentsArray.reduce((total, payment) => {
      const paidAmount = getPaidAmountForDateRange(
        payment,
        dateRangeStart,
        dateRangeEnd
      );
      return total + paidAmount;
    }, 0);
  };

  // Get paid amount for specific date range (helper for comparison)
  const getPaidAmountForDateRange = (
    payment: Payment,
    startDateStr: string,
    endDateStr: string
  ) => {
    if (!payment.installments || payment.installments.length === 0) {
      return payment.amount || 0;
    }

    const parseDate = (dateStr: string) => {
      const [day, month, year] = dateStr.split('-');
      return new Date(`${year}-${month}-${day}`);
    };

    const start = parseDate(startDateStr);
    const end = parseDate(endDateStr);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const total = payment.installments.reduce((sum, inst) => {
      const installmentDate = new Date(inst.date);
      installmentDate.setHours(0, 0, 0, 0);

      if (installmentDate >= start && installmentDate <= end) {
        return sum + (Number(inst.amount) || 0);
      }
      return sum;
    }, 0);

    // Fallback to sum of all installments if none found in range
    if (total === 0 && payment.installments.length > 0) {
      return payment.installments.reduce((sum, inst) => {
        return sum + (Number(inst.amount) || 0);
      }, 0);
    }

    return total;
  };

  // Get currency for display (assuming first payment's currency or default)
  const getDisplayCurrency = (paymentsArray: Payment[] = []) => {
    if (paymentsArray.length > 0) {
      const firstPayment = paymentsArray[0];
      if (
        typeof firstPayment.planId === 'object' &&
        firstPayment.planId !== null
      ) {
        return firstPayment.planId.currency || 'ALL';
      }
    }
    return 'ALL';
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'default';
      case 'partially_paid':
        return 'secondary';
      case 'free':
        return 'outline';
      case 'pending':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handleDeletePayment = async (id: string) => {
    confirmationStore.open({
      title: t('payments.confirmDelete'),
      message: t('payments.deleteConfirmation'),
      variant: 'destructive',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        try {
          await deletePaymentMutation.mutateAsync({ id, gymId: '' }); // gymId is still required by API

          // Force close the confirmation dialog
          confirmationStore.close();

          // Show success toast
          toast({
            title: t('common.success'),
            description: t('payments.paymentDeleted'),
          });

          // Force refetch payments to update the UI
          await queryClient.invalidateQueries({
            queryKey: ['payments', startDate, endDate],
          });
        } catch (error) {
          toast({
            title: t('common.error'),
            description: t('common.errorOccurred'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  const handleAddInstallment = (payment: Payment) => {
    setSelectedPayment(payment);
    setAddInstallmentDialogOpen(true);
  };

  const handleEditDate = (payment: Payment) => {
    setSelectedPayment(payment);
    setEditDateDialogOpen(true);
  };

  const handleDateChanged = async (paymentId: string, newDate: string) => {
    if (!paymentId) return;

    try {
      await updatePaymentMutation.mutateAsync({
        id: paymentId,
        payment: { date: newDate },
      });

      toast({
        title: t('common.success'),
        description: t('payments.dateUpdated', 'Payment date updated successfully'),
      });

      setEditDateDialogOpen(false);

      await queryClient.invalidateQueries({
        queryKey: ['payments', startDate, endDate],
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  const handleInstallmentAdded = async (
    paymentId: string,
    newInstallments: any[]
  ) => {
    if (!paymentId) return;

    try {
      await updatePaymentMutation.mutateAsync({
        id: paymentId,
        payment: {
          installments: newInstallments,
        },
      });

      toast({
        title: t('common.success'),
        description: t('payments.installmentAdded'),
      });

      setAddInstallmentDialogOpen(false);

      // Invalidate queries with the date range
      await queryClient.invalidateQueries({
        queryKey: ['payments', startDate, endDate],
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  if (paymentsLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!payments?.length) {
    return <div className="text-center py-4">{t('payments.noPayments')}</div>;
  }

  // Filter payments by status
  const filteredPayments =
    statusFilter === 'all'
      ? payments
      : payments.filter((payment: Payment) => payment.status === statusFilter);

  // Check if no payments after filtering
  if (filteredPayments.length === 0) {
    return (
      <div className="space-y-4">
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm font-medium">
            {t('common.filterByStatus', 'Filter by Status')}:
          </label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue
                placeholder={t('common.selectStatus', 'Select status')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
              <SelectItem value="paid">{t('payments.paid')}</SelectItem>
              <SelectItem value="partially_paid">
                {t('payments.partiallyPaid')}
              </SelectItem>
              <SelectItem value="free">{t('payments.free')}</SelectItem>
              <SelectItem value="pending">{t('payments.pending')}</SelectItem>
              <SelectItem value="failed">{t('payments.failed')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-center py-4">
          {statusFilter === 'all'
            ? t('payments.noPayments')
            : t(
                'payments.noPaymentsForStatus',
                `No payments found with status: ${statusFilter}`
              )}
        </div>
      </div>
    );
  }

  // Calculate pagination
  const totalItems = filteredPayments.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  return (
    <>
      {/* Payment Summary */}
      <div className="mb-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('payments.paymentSummary', 'Payment Summary')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    calculateTotalAmount(payments, startDate, endDate) / 100,
                    'EUR'
                  )}
                </p>
              </div>

              {comparisonMode && comparisonStartDate && comparisonEndDate && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('payments.comparisonPeriod', 'Comparison Period')}:{' '}
                    {comparisonStartDate} - {comparisonEndDate}
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      calculateTotalAmount(
                        comparisonPayments,
                        comparisonStartDate,
                        comparisonEndDate
                      ) / 100,
                      'EUR'
                    )}
                  </p>
                  <div className="mt-2">
                    {(() => {
                      const currentTotal = calculateTotalAmount(
                        payments,
                        startDate,
                        endDate
                      );
                      const comparisonTotal = calculateTotalAmount(
                        comparisonPayments,
                        comparisonStartDate,
                        comparisonEndDate
                      );
                      const difference = currentTotal - comparisonTotal;
                      const percentageChange =
                        comparisonTotal !== 0
                          ? (difference / comparisonTotal) * 100
                          : 0;

                      return (
                        <p
                          className={`text-sm font-medium ${
                            difference >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {difference >= 0 ? '+' : ''}
                          {formatCurrency(difference / 100, 'EUR')}(
                          {percentageChange.toFixed(1)}%)
                        </p>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Filter */}
      <div className="mb-4 flex items-center gap-2">
        <label htmlFor="status-filter" className="text-sm font-medium">
          {t('common.filterByStatus', 'Filter by Status')}:
        </label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={t('common.selectStatus', 'Select status')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
            <SelectItem value="paid">{t('payments.paid')}</SelectItem>
            <SelectItem value="partially_paid">
              {t('payments.partiallyPaid')}
            </SelectItem>
            <SelectItem value="free">{t('payments.free')}</SelectItem>
            <SelectItem value="pending">{t('payments.pending')}</SelectItem>
            <SelectItem value="failed">{t('payments.failed')}</SelectItem>
          </SelectContent>
        </Select>
        {statusFilter !== 'all' && (
          <span className="text-sm text-muted-foreground">
            {t('common.showing', 'Showing')} {totalItems}{' '}
            {t('common.results', 'results')}
          </span>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30px]"></TableHead>
            <TableHead>{t('payments.user')}</TableHead>
            <TableHead>{t('payments.planAmount')}</TableHead>
            <TableHead>{t('payments.paidAmount')}</TableHead>
            <TableHead>{t('payments.date')}</TableHead>
            <TableHead>{t('common.method')}</TableHead>
            <TableHead>{t('payments.plan')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedPayments.map((payment: Payment) => {
            const isExpanded = expandedRows[payment._id || ''] || false;
            const hasInstallments =
              payment.installments && payment.installments.length > 0;
            const planPrice = getPlanPrice(payment);
            const paidAmount = getPaidAmount(payment);

            return (
              <>
                <TableRow key={payment._id}>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => toggleRowExpansion(payment._id || '')}
                      disabled={!hasInstallments}
                    >
                      {hasInstallments &&
                        (isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        ))}
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium">
                    {getUserName(payment)}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(planPrice, getPlanCurrency(payment))}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(paidAmount, getPlanCurrency(payment))}
                    {payment.isOffer && (
                      <span className="text-sm text-muted-foreground ml-1">
                        ({t('payments.offer', 'Offer')})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{getDisplayDate(payment)}</TableCell>
                  <TableCell>{payment.method}</TableCell>
                  <TableCell>{getPlanName(payment)}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(payment.status)}>
                      {payment.status === 'paid' ||
                      payment.status === 'completed'
                        ? t('payments.paid')
                        : payment.status === 'partially_paid'
                        ? t('payments.partiallyPaid')
                        : payment.status === 'free'
                        ? t('payments.free')
                        : payment.status === 'pending'
                        ? t('payments.pending')
                        : t('payments.failed')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">
                            {t('common.openMenu')}
                          </span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleEditDate(payment)}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {t('payments.editDate', 'Edit Date')}
                        </DropdownMenuItem>
                        {planPrice > paidAmount && (
                          <DropdownMenuItem
                            onClick={() => handleAddInstallment(payment)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {t('payments.addInstallment')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDeletePayment(payment._id || '')}
                          className="text-destructive focus:text-destructive"
                        >
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>

                {/* Expanded row for installments */}
                {isExpanded && hasInstallments && (
                  <TableRow className="bg-muted/50">
                    <TableCell colSpan={9} className="p-0">
                      <div className="p-4">
                        <h4 className="text-sm font-medium mb-2">
                          {t('payments.installments')}
                        </h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>{t('common.amount')}</TableHead>
                              <TableHead>{t('payments.date')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {payment.installments?.map(
                              (installment: any, index: number) => (
                                <TableRow key={index}>
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>
                                    {formatCurrency(
                                      Number(installment.amount),
                                      getPlanCurrency(payment)
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {(() => {
                                      const date = new Date(installment.date);
                                      const dateStr = date.toLocaleDateString();
                                      const hours = date
                                        .getHours()
                                        .toString()
                                        .padStart(2, '0');
                                      const minutes = date
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0');
                                      return `${dateStr} ${hours}:${minutes}`;
                                    })()}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                className="font-bold text-right"
                              >
                                {t('payments.totalPaid')}:
                              </TableCell>
                              <TableCell className="font-bold">
                                {formatCurrency(
                                  paidAmount,
                                  getPlanCurrency(payment)
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>

      <DataTablePagination
        page={currentPage}
        pageCount={totalPages}
        total={totalItems}
        onPageChange={setCurrentPage}
      />

      {/* Add Installment Dialog */}
      {selectedPayment && (
        <AddInstallmentDialog
          open={addInstallmentDialogOpen}
          onOpenChange={setAddInstallmentDialogOpen}
          payment={selectedPayment}
          onInstallmentAdded={handleInstallmentAdded}
        />
      )}

      {/* Edit Payment Date Dialog */}
      {selectedPayment && (
        <EditPaymentDateDialog
          open={editDateDialogOpen}
          onOpenChange={setEditDateDialogOpen}
          payment={selectedPayment}
          onDateChanged={handleDateChanged}
        />
      )}
    </>
  );
}
